import { FlatTreeControl } from '@angular/cdk/tree';
import { AfterViewInit, ChangeDetectionStrategy, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { BehaviorSubject, Observable, of as observableOf } from 'rxjs';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ConfigService } from 'src/app/services/config/config.service';
import { ErrorReportingService } from '../../services/errorReporting/error-reporting.service';
import { BaseComponent } from 'src/app/base/base.component';
import { DataSourceType } from 'src/app/domainObjects/dataSourceType';
import { FileFlatNode, FileNode } from 'src/app/objectModel/fileNode';
import { OrganizationService } from 'src/app/views/organization/organization.service';
import { toFtpApiError } from 'src/app/views/settings/organization-settings/ftp-setup/org-ftp-service.model';

class FileSystemItemModel {
  id = 0;
  name = '';
  isFolder = false;
  files: FileSystemItemModel[] | null | undefined;

  constructor(id: number, name: string, isFolder: boolean, files: FileSystemItemModel[] | null) {
    this.id = id;
    this.name = name;
    this.isFolder = isFolder;
    this.files = files;
  }
}
@Component({
    selector: 'app-folder-browser-dialog',
    templateUrl: './folder-browser-dialog.component.html',
    styleUrls: ['./folder-browser-dialog.component.css'],
    standalone: false
})
export class FolderBrowserDialogComponent extends BaseComponent implements OnInit, AfterViewInit{
  dataSourceTypeId?: DataSourceType;
  supportedFileTypes?: string[];
  fileType?: string;
  /** When set, browse the folders of this organization FTP service instead of local storage. */
  ftpServiceId?: number;
  title = 'Browse for a folder to import';
  treeControl: FlatTreeControl<FileFlatNode>;
  treeFlattener: MatTreeFlattener<FileNode, FileFlatNode>;
  dataSource: MatTreeFlatDataSource<FileNode, FileFlatNode>;
  fullDatasource = [];
  filteredDatasource = [];

  isLoading = new BehaviorSubject<boolean>(false);

  @ViewChild(CdkVirtualScrollViewport) virtualScroll: CdkVirtualScrollViewport;
  files = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data,
    public dialogRef: MatDialogRef<FolderBrowserDialogComponent>,
    private configService: ConfigService,
    private organizationService: OrganizationService,
    matDialog: MatDialog,
    errorLogger: ErrorReportingService) {

    super(errorLogger, matDialog);

    this.dataSourceTypeId = data.dataSourceTypeId;
    this.supportedFileTypes = data.supportedFileTypes;
    this.fileType = this.supportedFileTypes?.length > 0 ? this.supportedFileTypes[0] : null;
    this.ftpServiceId = data.ftpServiceId ?? undefined;
    if (this.ftpServiceId != null) {
      this.title = 'Browse for an SFTP folder';
    }

    this.treeFlattener = new MatTreeFlattener(
      this.transformer,
      this._getLevel,
      this._isExpandable,
      this._getChildren);

    this.treeControl = new FlatTreeControl<FileFlatNode>(
      this._getLevel,
      this._isExpandable);
  }

  transformer = (node: FileNode, level: number) => {
    return new FileFlatNode(!!node.children, node.path, node.name, level);
  }

  private _getLevel = (node: FileFlatNode) => node.level;
  private _isExpandable = (node: FileFlatNode) => node.expandable;
  private _getChildren = (node: FileNode): Observable<FileNode[]> =>
    observableOf(node.children)

  hasChild = (_: number, _nodeData: FileFlatNode) => _nodeData.expandable;

  async ngOnInit(): Promise<void> {
    this.isLoading.next(true);

    try {
      const organizationId = 1;
      const response = this.ftpServiceId != null
        ? await this.browseFtpFolders(this.ftpServiceId)
        : await this.configService.browseFoldersAsync(organizationId);
      //console.log(`Response: ${response.items}`);

      this.fullDatasource = [...response.items]
        .filter((t) => {
          return (t.isFolder && t.children?.length > 0) || !t.isFolder;
        })
        .map((item) => {
          return { ...item, filename: item.name, children: item.children };
        });

      //this.filteredDatasource = this.filterNodeRecursive([...this.fullDatasource]);

      this.dataSource = new MatTreeFlatDataSource(
        this.treeControl,
        this.treeFlattener);

      //console.log("this.filteredDatasource :", [...this.fullDatasource]);
      this.dataSource.data = [...this.fullDatasource];

      // this.virtualScroll.renderedRangeStream.subscribe(range => {
      //   //console.log(range, 'range');
      //   this.dataSource.data = this.fullDatasource.slice(range.start, range.end);
      // });

    } catch (err) {
      this.displayError(this.ftpServiceId != null && err?.message ? err.message : 'Failed to get folders', 'Error', err, (result) => {
        this.dialogRef.close();
      });
    } finally {
      this.isLoading.next(false);
    }
  }

  private async browseFtpFolders(ftpServiceId: number): Promise<any> {
    let result;
    try {
      result = await this.organizationService.browseOrgFtpServiceFolders(ftpServiceId);
    } catch (err) {
      throw new Error(toFtpApiError(err).message);
    }
    if (result?.errorMessage) {
      throw new Error(result.errorMessage);
    }
    return { items: result?.items ?? [] };
  }

  ngAfterViewInit(): void {

  }

  onFilterChanged(event: any): void {
    this.filteredDatasource = this.filterNodeRecursive([...this.fullDatasource]);
    this.dataSource.data = this.filteredDatasource;
  }

  filterNodeRecursive(nodes: FileNode[]): FileNode[] {
    const items = [];

    for (const x of nodes) {
      const parts = x.name.split('.');
      const ext = parts[parts.length - 1];

      if (this.fileType.endsWith('*')) {
        const extAny = this.fileType.substring(0, this.fileType.length - 1);
        if ('.' + ext.startsWith(extAny) && !x.isFolder) {
          items.push(x);
        }
      }
      else if ('.' + ext === this.fileType && !x.isFolder) {
        items.push(x);
      }

      if (x.children?.length > 0 && x.isFolder) {
        // add filtered children nodes
        const children = this.filterNodeRecursive(x.children);
        if (children.length > 0) {
          // add the folder
          items.push(x);

          x.children = children;
        }
      }
    }

    return items;
  }

  onFileSelected(node: FileFlatNode): void {
    //console.log('you selected ' + node.path + '/' + node.filename);
    this.dialogRef.close({ selectedFileName: node.path });
  }
}
